import Qs from 'qs'

import getToken, { checkAuthToken } from './index'
import { fixJSON } from './fixJsonHelpers'

export async function madeSocketRequest(
  method,
  { queryParams, params, data, url, requestId, shouldParseJsonStream },
  handlers
) {
  const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
  // proxy doesn't work with wss, so we need to use dev.partnerbrite.com
  const isProxy = window.location.hostname.includes('localhost') || window.location.hostname.includes('features')
  const host = isProxy ? 'dev.partnerbrite.com' : window.location.host
  const path = protocol + host + url

  const token = await getToken()
  // check token before request
  checkAuthToken(token, url)

  const requestParams = {
    ...queryParams,
    authorization: `Bearer ${token}`
  }
  const serializedParams = Qs.stringify(requestParams, { arrayFormat: 'repeat' })
  const requestUrl = path + '?' + serializedParams

  return apiStreamSocket({ method, url: requestUrl, params, data, requestId, shouldParseJsonStream }, handlers)
}

export async function apiStreamSocket({ method, url, params, data, requestId, shouldParseJsonStream }, handlers) {
  const { handleStreamUpdate: onStreamMessage, onSocketOpen } = handlers

  return new Promise((resolve, reject) => {
    // setting request timeout - reject if no response in 30 seconds
    let hasReturned = false
    setTimeout(function () {
      if (!hasReturned) {
        reject()
      }
    }, 30000)

    try {
      const socket = new WebSocket(url)

      socket.addEventListener('open', function (event) {
        hasReturned = true
        onSocketOpen && onSocketOpen(socket)
        // send payload with request type once's socket connection is established
        // but there are cases when socket endpoint doesn't require the initial request message from FE
        if (method) {
          // If params has request_id, request_type, and params properties, it's using the complex structure
          if (params && params.request_id && params.request_type && params.params) {
            socket.send(JSON.stringify(params))
          } else {
            // Otherwise, use the simple structure
            socket.send(
              JSON.stringify({
                request_id: requestId,
                request_type: method,
                params,
                data
              })
            )
          }
        }
      })

      const CHUNKS_LENGTH_WHEN_SHOULD_BE_UPDATED = 20
      const PARSE_JSON_ATTEMPTS = 4
      const initialBuffer = {
        text: '',
        conversationId: '',
        requestId: '',
        chunksLength: 0
      }
      let buffer = {
        ...initialBuffer
      }
      // Counter for failed JSON parsing attempts
      let jsonParseFailedAttempts = 0

      function handleStreamMessages(socket, resolve, reject) {
        socket.addEventListener('message', function (event) {
          const data = JSON.parse(event.data)

          // each websocket message contains a data to specific request_id
          // as there could be multiple websockets requests at the same time we need to filter by correct request_id
          if (requestId === data.request_id) {
            const chunk = data?.data?.chunk

            // Store the request_id from the data
            buffer.requestId = data.request_id

            if (chunk) {
              buffer.text += chunk.text
              buffer.chunksLength += 1
              buffer.conversationId = chunk.conversation_id
            }

            if (
              // we update the redux store only when the buffer is updated 20 times
              buffer.chunksLength === CHUNKS_LENGTH_WHEN_SHOULD_BE_UPDATED ||
              // or if we reached the end of the stream
              data?.status === 'completed'
            ) {
              if (shouldParseJsonStream) {
                try {
                  const fixedJson = fixJSON(buffer.text)
                  const parsedJson = JSON.parse(fixedJson)
                  onStreamMessage && onStreamMessage(parsedJson, socket)
                  // Reset failed attempts counter on successful parse
                  jsonParseFailedAttempts = 0
                } catch (error) {
                  console.error('Error parsing JSON:', error)
                  console.error('Buffer text:', buffer.text)

                  // Increment failed attempts counter
                  jsonParseFailedAttempts++

                  // Only reject if we've exceeded the maximum number of attempts
                  // or if we've reached the end of the stream
                  if (jsonParseFailedAttempts >= PARSE_JSON_ATTEMPTS || data?.status === 'completed') {
                    console.error(`Failed to parse JSON after ${jsonParseFailedAttempts} attempts. Rejecting.`)
                    reject(error)
                  } else {
                    console.log(
                      `Failed to parse JSON (attempt ${jsonParseFailedAttempts}/${PARSE_JSON_ATTEMPTS}). Continuing to collect more chunks...`
                    )
                    // Don't reset chunksLength to allow buffer to grow larger
                    return
                  }
                }
              } else {
                onStreamMessage && onStreamMessage(buffer, socket)
              }

              // reset buffer chunksLength
              buffer.chunksLength = 0
            }

            if (data?.status === 'completed') {
              handleServerResponse({
                socket,
                resolve,
                responseBuffer: buffer.text
              })

              // reset buffer
              buffer = {
                ...initialBuffer
              }
            }
          } else if (method === null) {
            // if there is no request_id, we need to handle the message as a stream
            // so we need to call the handler for each message
            onStreamMessage(data, socket)
          }
        })
      }
      handleStreamMessages(socket, resolve, reject)
      handleServerError(socket, reject)
    } catch (error) {
      hasReturned = true
      reject(error)
    }
  })
}

function handleServerResponse({ socket, resolve, responseBuffer }) {
  socket.close()

  resolve(responseBuffer)
}

function handleServerError(socket, reject) {
  socket.addEventListener('error', function (event) {
    console.log('event', event)
    console.error(`WebSocket error occurred: ${event.type}`)
    console.error(`WebSocket state: ${socket.readyState}`)
    console.error(`WebSocket URL: ${socket.url}`)
    console.error(`WebSocket error message: ${event.message}`)
    reject(event.message)
    socket.close()
  })
}
